import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { LockClosedIcon, LockOpenIcon } from "@heroicons/react/24/solid";
import * as Yup from "yup";
import {
  fetchUsers,
  saveUser,
  updateUser,
} from "../../features/admin/UserSlice";
import { toast } from "react-toastify";
import { fetchRoles } from "../../features/admin/RoleSlice";
import AvatarImageModal from "./AvatarImageModal";
import {  capitalizeWords } from "../../utils/helps";
import apiCustom from "../../utils/ApiCustom";
import DatePicker from "../DatePicker";
import { PencilSquareIcon, PhotoIcon } from "@heroicons/react/24/outline";

let userSchema;
const updateUserSchema = Yup.object().shape({
  userFullName: Yup.string().trim().required("Họ tên không được để trống"),
  userEmail: Yup.string()
    .email("Email không hợp lệ")
    .required("Email không được để trống"),
  // .required("Email không được để trống"),
  userPhone: Yup.string().matches(
    /^[0-9]{10}$/,
    "Số điện thoại phải có 10 chữ số"
  ),
  // .required("Số điện thoại không được để trống"),
  userAddress: Yup.string().trim(),
  userCode: Yup.string().trim(),
  account: Yup.string().trim().required("Tài khoản không được để trống"),
  roleId: Yup.number()
    .typeError("Vai trò không hợp lệ")
    .moreThan(0, "Vai trò không được để trống")
    .required("Vai trò không được để trống"),
  placeOfBirth: Yup.string().trim(),
  dateOfBirth: Yup.date().typeError("Ngày sinh không hợp lệ"),
  idCard: Yup.string().trim().matches(/^\d*$/, "Số căn cước không hợp lệ"),
});

const userPasswordSchema = Yup.object().shape({
  password: Yup.string()
    .min(8, "Mật khẩu phải có ít nhất 8 ký tự")
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/,
      "Mật khẩu phải có ít nhất 1 chữ hoa, 1 chữ thường, 1 số và 1 ký tự đặc biệt"
    )
    .required("Mật khẩu không được để trống"),
});

const fieldLabels = {
  userFullName: "Họ và tên",
  userEmail: "Email",
  userPhone: "Số điện thoại",
  userAddress: "Địa chỉ",
  userCode: "Mã người dùng",
  account: "Tài khoản",
  roleId: "Chức vụ tại BCCU",
  userAvatar: "Ảnh đại diện",
  userProfile: "Ảnh hồ sơ",
  placeOfBirth: "Nơi sinh",
  dateOfBirth: "Ngày sinh",
  idCard: "Số căn cước",
  password: "Mật khẩu",
};

const UserModal = ({ userIdProp, onClose }) => {
  const [formData, setFormData] = useState({
    roleId: 0,
    userCode: "",
    userFullName: "",
    userEmail: "",
    userPhone: "",
    userAvatar: "",
    userProfile: "",
    userAddress: "",
    account: "",
    placeOfBirth: "",
    dateOfBirth: "",
    idCard: "",
    password: "",
    rePassword: "",
    type: "",
    nonEditable: true,
  });

  const [errors, setErrors] = useState({});
  const [avatarPreview, setAvatarPreview] = useState("");
  const [profilePreview, setProfilePreview] = useState("");
  const [hasNewAvatar, setHasNewAvatar] = useState(false);
  const [hasNewProfile, setHasNewProfile] = useState(false);
  const dispatch = useDispatch();
  const { roles } = useSelector((state) => state.roles);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [lockUpdate, setLockUpdate] = useState(null);
  const [user, setUser] = useState(null);

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleOpenProfileModal = () => {
    setIsProfileModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleCloseProfileModal = () => {
    setIsProfileModalOpen(false);
  };

  const handlePressAvatar = (media) => {
    setIsModalOpen(false);
    setHasNewAvatar(true);
    const avatarPreviewChange = media.url;
    setAvatarPreview(avatarPreviewChange);
    setFormData({ ...formData, userAvatar: media.url });
  };

  const handlePressProfile = (media) => {
    setIsProfileModalOpen(false);
    setHasNewProfile(true);
    const profilePreviewChange = media.url;
    setProfilePreview(profilePreviewChange);
    setFormData({ ...formData, userProfile: media.url });
  };

  useEffect(() => {
    if (userIdProp) {
      getUser();
    }
  }, []);

  const getUser = () => {
    apiCustom
      .get(`/user-show/${userIdProp}`)
      .then((res) => {
        setUser(res.data?.data?.data);
      })
      .catch((err) => console.log(err));
  };

  useEffect(() => {
    userSchema = updateUserSchema;

    if (user) {
      setFormData({
        roleId: user.roleId || 0,
        userCode: user.userCode || "",
        userFullName: user.userFullName || "",
        userEmail: user.userEmail || "",
        userPhone: user.userPhone || "",
        userAvatar: user.userAvatar || "",
        userProfile: user.userProfile || "",
        userAddress: user.userAddress || "",
        account: user.account || "",
        placeOfBirth: user.placeOfBirth || "",
        dateOfBirth: user.dateOfBirth || "",
        idCard: user.idCard || "",
        password: "",
        type: user.type || "user",
        nonEditable: user.nonEditable ?? true,
      });
      const lock = user.nonEditable ?? true;
      if (userId == user?.id && userId != 1) {
        setLockUpdate(lock);
      } else {
        setLockUpdate(hasPermission("USER_UPDATE") ? lock : true);
      }

      setHasNewAvatar(!!user.userAvatar);
      if (user.userAvatar) {
        const avatarPreviewChange = user.userAvatar;
        setAvatarPreview(avatarPreviewChange);
      }

      setHasNewProfile(!!user.userProfile);
      if (user.userProfile) {
        const profilePreviewChange = user.userProfile;
        setProfilePreview(profilePreviewChange);
      }
    } else {
      userSchema = updateUserSchema.concat(userPasswordSchema);
    }
  }, [user]);

  const getUserPermissions = () => {
    const permissions = localStorage.getItem("permissions");
    return permissions
      ? JSON.parse(permissions)?.map((p) => p.permissionSlug)
      : [];
  };

  const hasPermission = (requiredPermission) => {
    const userId = localStorage.getItem("userId");
    if (userId == 1) {
      return true;
    }
    const userPermissions = getUserPermissions();
    return userPermissions.includes(requiredPermission);
  };

  useEffect(() => {
    dispatch(fetchRoles());
  }, [dispatch]);

  const getAvatarUrl = () => {
    if (hasNewAvatar) {
      return avatarPreview;
    } else if (user && user.userAvatar) {
      return `${process.env.REACT_APP_API_URL}media/${user.userAvatar}`;
    }
    return "";
  };

  const getProfileUrl = () => {
    if (hasNewProfile) {
      return profilePreview;
    } else if (user && user.userProfile) {
      return `${user.userProfile}`;
    }
    return "";
  };

  const handleChange = async (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));

    try {
      await Yup.reach(userSchema, name).validate(value);
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    } catch (validationError) {
      setErrors((prev) => ({ ...prev, [name]: validationError.message }));
    }
  };

  const handleLockUpdate = async ({ useToast = true }) => {
    try {
      const response = await apiCustom.put(
        `/user-lock-update`,
        {
          id: user.id,
          userCode: formData.userCode,
          userFullName: formData.userFullName,
          nonEditable: !lockUpdate,
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      if (response.status === 200) {
        setLockUpdate(!lockUpdate);
        setFormData((prev) => ({ ...prev, nonEditable: !lockUpdate }));

        if (useToast) {
          if (!lockUpdate) {
            toast.success("Đã khoá thông tin người dùng!");
          } else {
            toast.success("Đã mở khoá thông tin người dùng!");
          }
        }
      }
    } catch (error) {
      toast.error("Đã xảy ra lỗi!");
    } finally {
      // dispatch(fetchUsers());
      // onClose();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    let validationSchema = userSchema;
    try {
      await validationSchema.validate(formData, { abortEarly: false });

      formData["userFullName"] = capitalizeWords(formData["userFullName"]);

      if (user) {
        const updateData = { ...formData, id: user.id };

        await dispatch(
          updateUser({ id: user.id, userData: updateData })
        ).unwrap();
        await handleLockUpdate({ useToast: false });
        toast.success("Cập nhật thành công!");
        dispatch(fetchUsers());
        onClose();
      } else {
        const newUserData = {
          ...formData,
          rePassword: formData.password,
        };
        await dispatch(saveUser(newUserData)).unwrap();
        toast.success("Thêm người dùng mới thành công!");
        onClose();
      }
    } catch (error) {
      if (error.status === 422) {
        setErrors((prev) => ({
          ...prev,
          ...error.messages,
        }));
      } else if (error instanceof Yup.ValidationError) {
        const formErrors = {};
        error.inner.forEach((err) => {
          formErrors[err.path] = err.message;
        });
        setErrors(formErrors);
      }
    }
  };

  const userId = localStorage.getItem("userId");
  const checkIndividualDontHavePermission = (permission) => {
    if (userId != 1 && !hasPermission(permission)) {
      return true;
    }
    return false;
  };

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-50 flex justify-center items-center z-50 p-4 overflow-y-auto">
      <div className="bg-base-100 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="">
          <div className="flex sticky top-0 bg-base-100 z-10 p-6 justify-between items-center">
            <h2 className="text-2xl font-bold">
              {user ? "Chỉnh sửa thông tin người dùng" : "Thêm người dùng mới"}
            </h2>
            <button
              onClick={() => {
                dispatch(fetchUsers());
                onClose();
              }}
              className="px-4 py-2 border rounded-md shadow-sm text-sm font-medium text-base-700 bg-base-100 hover:bg-base-200"
            >
              X
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="px-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-6">
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">
                        {fieldLabels.userCode}
                      </label>
                      <input
                        type="text"
                        name="userCode"
                        disabled={lockUpdate}
                        value={formData.userCode}
                        onChange={handleChange}
                        className={`w-full p-2 border rounded-md ${
                          errors.userCode ? "border-red-500" : "border-gray-300"
                        } focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                      />
                      {errors.userCode && (
                        <p className="mt-1 text-sm text-red-600">
                          {errors.userCode}
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">
                        {fieldLabels.userFullName}
                        <i className="text-red-600"> *</i>
                      </label>
                      <input
                        style={{ textTransform: "capitalize" }}
                        type="text"
                        name="userFullName"
                        disabled={lockUpdate}
                        value={formData.userFullName}
                        onChange={handleChange}
                        className={`w-full p-2 border rounded-md ${
                          errors.userFullName
                            ? "border-red-500"
                            : "border-gray-300"
                        } focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                      />
                      {errors.userFullName && (
                        <p className="mt-1 text-sm text-red-600">
                          {errors.userFullName}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1">
                        {fieldLabels.userPhone}
                        <i className="text-red-600"> *</i>
                      </label>
                      <input
                        type="text"
                        name="userPhone"
                        disabled={lockUpdate}
                        value={formData.userPhone}
                        onChange={handleChange}
                        className={`w-full p-2 border rounded-md ${
                          errors.userPhone
                            ? "border-red-500"
                            : "border-gray-300"
                        } focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                      />
                      {errors.userPhone && (
                        <p className="mt-1 text-sm text-red-600">
                          {errors.userPhone}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1">
                        {fieldLabels.userEmail}
                        <i className="text-red-600"> *</i>
                      </label>
                      <input
                        type="email"
                        name="userEmail"
                        disabled={lockUpdate}
                        value={formData.userEmail}
                        onChange={handleChange}
                        className={`w-full p-2 border rounded-md ${
                          errors.userEmail
                            ? "border-red-500"
                            : "border-gray-300"
                        } focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                      />
                      {errors.userEmail && (
                        <p className="mt-1 text-sm text-red-600">
                          {errors.userEmail}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1">
                        {fieldLabels.userAddress}
                      </label>
                      <input
                        type="text"
                        name="userAddress"
                        disabled={lockUpdate}
                        value={formData.userAddress}
                        onChange={handleChange}
                        className={`w-full p-2 border rounded-md ${
                          errors.userAddress
                            ? "border-red-500"
                            : "border-gray-300"
                        } focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                      />
                      {errors.userAddress && (
                        <p className="mt-1 text-sm text-red-600">
                          {errors.userAddress}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                <div className="space-y-6">
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">
                        {fieldLabels.idCard}
                      </label>
                      <input
                        type="text"
                        name="idCard"
                        disabled={lockUpdate}
                        value={formData.idCard}
                        onChange={handleChange}
                        className={`w-full p-2 border rounded-md ${
                          errors.idCard ? "border-red-500" : "border-gray-300"
                        } focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                      />
                      {errors.idCard && (
                        <p className="mt-1 text-sm text-red-600">
                          {errors.idCard}
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">
                        {fieldLabels.placeOfBirth}
                      </label>
                      <input
                        type="text"
                        name="placeOfBirth"
                        disabled={lockUpdate}
                        value={formData.placeOfBirth}
                        onChange={handleChange}
                        className={`w-full p-2 border rounded-md ${
                          errors.placeOfBirth
                            ? "border-red-500"
                            : "border-gray-300"
                        } focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                      />
                      {errors.placeOfBirth && (
                        <p className="mt-1 text-sm text-red-600">
                          {errors.placeOfBirth}
                        </p>
                      )}
                    </div>
                    {/* <div>
                    <label className="block text-sm font-medium mb-1">
                      {fieldLabels.dateOfBirth}
                      <i className="text-red-600"> *</i>
                    </label>
                    <input
                      type="date"
                      name="dateOfBirth"
                      disabled={lockUpdate}
                      value={formatDateReverse(formData.dateOfBirth ?? "")}
                      onChange={handleChange}
                      className={`w-full p-2 border rounded-md ${
                        errors.dateOfBirth
                          ? "border-red-500"
                          : "border-gray-300"
                      } focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                    />
                    {errors.dateOfBirth && (
                      <p className="mt-1 text-sm text-red-600">
                        {errors.dateOfBirth}
                      </p>
                    )}
                  </div> */}
                    <div>
                      <label className="block text-sm font-medium mb-1">
                        {fieldLabels.dateOfBirth}
                        <i className="text-red-600"> *</i>
                      </label>
                      <DatePicker
                        disabled={lockUpdate}
                        name="dateOfBirth"
                        value={formData["dateOfBirth"]}
                        onChange={(name, value) => {
                          handleChange({
                            target: {
                              name,
                              value,
                            },
                          });
                        }}
                        title={fieldLabels.dateOfBirth}
                        error={errors["dateOfBirth"]}
                      />
                      {errors["dateOfBirth"] && (
                        <p className="text-red-500 text-sm">
                          {errors["dateOfBirth"]}
                        </p>
                      )}
                    </div>

                    <div>
                      <label
                        className="block text-sm font-medium"
                        style={{ marginBottom: "6px" }}
                      >
                        {fieldLabels.roleId}
                        <i className="text-red-600"> *</i>
                      </label>
                      <select
                        name="roleId"
                        disabled={
                          lockUpdate ||
                          checkIndividualDontHavePermission("USER_UPDATE")
                        }
                        value={formData.roleId}
                        onChange={(e) => {
                          setFormData({
                            ...formData,
                            roleId:
                              e.target.value === ""
                                ? 0
                                : parseInt(e.target.value),
                          });
                          setErrors({
                            ...errors,
                            roleId: "",
                          });
                        }}
                        className={`w-full p-2 border rounded-md ${
                          errors.roleId ? "border-red-500" : "border-gray-300"
                        } focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                      >
                        <option value="">Chọn vai trò</option>
                        {roles &&
                          roles.map((role) => (
                            <option key={role.id} value={role.id}>
                              {role.roleName}
                            </option>
                          ))}
                      </select>
                      {errors.roleId && (
                        <p className="mt-1 text-sm text-red-600">
                          {errors.roleId}
                        </p>
                      )}
                    </div>
                    {user && (
                      <div>
                        <label className="block text-sm font-medium mb-1">
                          {fieldLabels.account}
                          <i className="text-red-600"> *</i>
                        </label>
                        <input
                          type="text"
                          name="account"
                          disabled={lockUpdate}
                          value={formData.account}
                          onChange={handleChange}
                          className={`w-full p-2 border rounded-md ${
                            errors.account
                              ? "border-red-500"
                              : "border-gray-300"
                          } focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                        />
                        {errors.account && (
                          <p className="mt-1 text-sm text-red-600">
                            {errors.account}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              {!user && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4 mt-6 border-t">
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      {fieldLabels.account}
                      <i className="text-red-600"> *</i>
                    </label>
                    <input
                      type="text"
                      name="account"
                      disabled={lockUpdate}
                      value={formData.account}
                      onChange={handleChange}
                      className={`w-full p-2 border rounded-md ${
                        errors.account ? "border-red-500" : "border-gray-300"
                      } focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                    />
                    {errors.account && (
                      <p className="mt-1 text-sm text-red-600">
                        {errors.account}
                      </p>
                    )}
                  </div>
                  <div className="z-0">
                    <label className="block text-sm font-medium mb-1">
                      {fieldLabels.password}
                      <i className="text-red-600"> *</i>
                    </label>
                    <div className="relative">
                      <input
                        type={passwordVisible ? "text" : "password"}
                        name="password"
                        disabled={lockUpdate}
                        value={formData.password}
                        onChange={handleChange}
                        className={`w-full p-2 border rounded-md ${
                          errors.password ? "border-red-500" : "border-gray-300"
                        } focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                      />
                      <button
                        type="button"
                        onClick={() => setPasswordVisible(!passwordVisible)}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      >
                        {passwordVisible ? "Ẩn" : "Hiện"}
                      </button>
                    </div>
                    {errors.password && (
                      <p className="mt-1 text-sm text-red-600">
                        {errors.password}
                      </p>
                    )}
                  </div>
                </div>
              )}
              <div className="flex py-6 justify-start">
                <input
                  type="checkbox"
                  checked={formData.type === "admin"}
                  disabled={lockUpdate || !hasPermission("APPROVE_USER")}
                  onChange={(e) => {
                    setFormData((prev) => ({
                      ...prev,
                      type: e.target.checked ? "admin" : "user",
                    }));
                  }}
                  className={`checkbox checkbox-info`}
                />
                <label className="block text-sm font-medium mb-1 ml-4">
                  Ban điều hành
                </label>
              </div>
              <div className="space-y-6 pt-4 border-t items-center grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  {/* Avatar section */}
                  <label className="block text-sm font-medium mb-1" >
                    {fieldLabels.userAvatar}
                    <i className="text-red-600"> *</i>
                  </label>
                  <div className="mt-1 flex items-center space-x-4">
                    <button
                      type="button"
                      disabled={lockUpdate}
                      onClick={handleOpenModal}
                      className={`relative w-32 h-32 border-2 border-gray-300 border-dashed rounded-lg overflow-hidden ${
                        lockUpdate
                          ? "cursor-default"
                          : "cursor-pointer group hover:border-sky-500"
                      }`}
                    >
                      {getAvatarUrl() && hasNewAvatar ? (
                        <img
                          src={getAvatarUrl()}
                          alt="Avatar Preview"
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-gray-400">
                          <PhotoIcon className="w-10 h-10 text-gray-300" />
                        </div>
                      )}
                      {!lockUpdate && (
                        <span className="absolute bottom-2 right-2 flex h-10 w-10 items-center justify-center rounded-full bg-sky-500 text-white shadow-md group-hover:bg-sky-600">
                          <PencilSquareIcon className="h-6 w-6" />
                        </span>
                      )}
                    </button>
                  </div>
                </div>

                {/* Profile section */}
                <div className="space-y-4" style={{ marginTop: "0px" }}>
                  <label className="block text-sm font-medium mb-1">
                    {fieldLabels.userProfile}
                  </label>
                  <div className="mt-1 flex items-center space-x-4 ">
                    <button
                      type="button"
                      disabled={lockUpdate}
                      onClick={handleOpenProfileModal}
                      className={`relative w-32 h-32 border-2 border-gray-300 border-dashed rounded-lg overflow-hidden ${
                        lockUpdate
                          ? "cursor-default"
                          : "cursor-pointer group hover:border-sky-500"
                      }`}
                    >
                      {getProfileUrl() && hasNewProfile ? (
                        <img
                          src={getProfileUrl()}
                          alt="Profile Preview"
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-gray-400">
                          <PhotoIcon className="w-10 h-10 text-gray-300" />
                        </div>
                      )}

                      {!lockUpdate && (
                        <span className="absolute bottom-2 right-2 flex h-10 w-10 items-center justify-center rounded-full bg-sky-500 text-white shadow-md group-hover:bg-sky-600">
                          <PencilSquareIcon className="h-6 w-6" />
                        </span>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
            {/* Footer - Action Buttons */}
            <div
              className={`flex sticky bottom-0 bg-base-100 z-10 p-6 pt-0 ${
                user ? "justify-between" : "justify-end"
              } border-t`}
            >
              {/* Lock update section */}
              <div>
                {user && hasPermission("USER_UPDATE") && (
                  <div className="space-x-4 pt-4">
                    <button
                      type="button"
                      onClick={handleLockUpdate}
                      className={`px-4 py-2 border rounded-md shadow-sm text-sm font-medium flex items-center space-x-2 btn-warning hover:bg-yellow-500`}
                    >
                      {lockUpdate ? (
                        <>
                          <LockClosedIcon className="h-5 w-5" />
                          <span>Đã khoá thông tin</span>
                        </>
                      ) : (
                        <>
                          <LockOpenIcon className="h-5 w-5" />
                          <span>Khoá thông tin</span>
                        </>
                      )}
                    </button>
                  </div>
                )}
              </div>
              <div className="flex justify-end space-x-4 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    dispatch(fetchUsers());
                    onClose();
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  {lockUpdate ? "Đóng" : "Huỷ"}
                </button>
                {!lockUpdate && (
                  <button
                    type="submit"
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                  >
                    {user ? "Cập nhật" : "Thêm mới"}
                  </button>
                )}
              </div>
            </div>
          </form>
        </div>
      </div>

      {isModalOpen && (
        <AvatarImageModal
          imageableId={userId}
          imageableType={"user"}
          imageType={"avatar"}
          avatarType={true}
          onClose={handleCloseModal}
          onPress={handlePressAvatar}
        />
      )}

      {isProfileModalOpen && (
        <AvatarImageModal
          imageableId={userId}
          imageableType={"user"}
          imageType={"profile"}
          avatarType={true}
          onClose={handleCloseProfileModal}
          onPress={handlePressProfile}
        />
      )}
    </div>
  );
};

export default UserModal;
